// src/pages/alumni/AlumniDashboard.js
import { useState, useEffect, useContext } from 'react';
import axios from '../../api/axios';
import {
  Box,
  Typography,
  Avatar,
  Paper,
  Modal,
  List,
  ListItem,
  ListItemText,
  useMediaQuery,
} from '@mui/material';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import './AlumniDashboard.css';
import { AuthContext } from '../../context/AuthContext';
import axiosInstance from '../../api/axios';

// Build a local Date from the event's date + a time string ("H:MM", "HH:MM", "HH:MM:SS")
function toLocalDate(dateInput, timeStr) {
  if (!dateInput) return null;
  const base = new Date(dateInput);
  const y = base.getFullYear(), m = base.getMonth(), d = base.getDate();
  let hh = 0, mm = 0;
  if (typeof timeStr === 'string') {
    const m2 = timeStr.trim().match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
    if (m2) { hh = Number(m2[1]); mm = Number(m2[2]); }
  }
  return new Date(y, m, d, hh, mm, 0, 0); // local time
}

function formatEventTimeRange(event) {
  const start = toLocalDate(event.date, event.startTime);
  const end = event.endTime ? toLocalDate(event.date, event.endTime) : null;

  if (start && end) {
    const s = start.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true });
    const e = end.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true });
    return `${s} – ${e}`;
  }
  if (start) {
    return start.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true });
  }
  return 'All day';
}


const API_BASE_URL = axiosInstance.defaults.baseURL;


const AlumniDashboard = () => {
  // WebSocket connection
  useEffect(() => {
    const timeout = setTimeout(() => {
      const socket = new WebSocket(`ws://${window.location.hostname}:5001/ws`);

      socket.onopen = () => {
        // console.log('✅ WebSocket connected');
        socket.send('Hello server');
      };

      // socket.onmessage = (event) => {
      //   console.log('📩 Message from server:', event.data);
      // };

      socket.onclose = () => {
        console.log('❌ WebSocket disconnected');
      };
    }, 200); // delay connection

    return () => clearTimeout(timeout);
  }, []);
  const { user } = useContext(AuthContext);
  const isTabletOrBelow = useMediaQuery('(max-width: 850px)');
  const [currentSlide, setCurrentSlide] = useState(0);
  const [events, setEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [carouselSlides, setCarouselSlides] = useState([]);

  useEffect(() => {
    const fetchPublications = async () => {
      try {
        const res = await axios.get('/api/publications');
        const slides = res.data.map((item) => ({
          image: `${API_BASE_URL}/${item.image?.filePath}`,
          title: item.title,
          description: item.description,
        }));
        setCarouselSlides(slides);
      } catch (err) {
        console.error('Failed to fetch publications for carousel:', err);
      }
    };

    fetchPublications();
  }, []);

  useEffect(() => {
    if (carouselSlides.length === 0) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) =>
        prev === carouselSlides.length - 1 ? 0 : prev + 1,
      );
    }, 5000);

    return () => clearInterval(interval);
  }, [carouselSlides.length]);

useEffect(() => {
  if (!user?.active) return;

  const fetchEvents = async () => {
    try {
      const response = await axios.get('/api/events');
      // Only include events where draft is false
      const filtered = response.data.filter(event => event.draft === false);
      setEvents(filtered);
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };

  fetchEvents();
}, [user]);

const eventsOnSelectedDate = events.filter(
  (event) =>
    selectedDate &&
    new Date(event.date).toDateString() === selectedDate.toDateString()
);


  const handleDateClick = (date) => {
    setSelectedDate(date);
    setModalOpen(true);
  };


const tileContent = ({ date }) => {
  const eventCount = events.filter(
    (event) =>
      event.draft === false &&
      new Date(event.date).toDateString() === date.toDateString()
  ).length;

  return (
    <div className="dots-container">
      {[...Array(eventCount)].map((_, i) => (
        <span key={i} className="event-dot" />
      ))}
    </div>
  );
};


  if (!user) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <Typography variant="h6">Loading your dashboard...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', height: '100vh', flexDirection: 'row' }}>
      {/* <Sidebar /> */}
      <Box
        sx={{
          width: '100%',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
        }}
      >
        <Paper
          className="dashboard-header"
          elevation={0}
          square
          sx={{ p: 3, mb: 2 }}
        >
          <Typography variant="h5" fontWeight="bold" fontSize={18}>
            Welcome to SMU Alumni Portal, {user.firstName} {user.lastName}
          </Typography>
        </Paper>

        {/* Layout Adjusted for Mobile vs Desktop */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: isTabletOrBelow ? 'column' : 'row',
            gap: 2,
            flexGrow: 1,
            overflowY: 'auto',
          }}
        >
          {/* Profile + Calendar come first on mobile */}
          <Box
            sx={{
              flex: isTabletOrBelow ? '1 1 100%' : 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <Paper
              elevation={0}
              square
              sx={{
                p: 3,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <Avatar sx={{ width: 100, height: 100 }}>
                <img
                  src={`${API_BASE_URL}/${user.photo?.filePath}`}
                  alt="profile"
                  crossOrigin="anonymous"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </Avatar>

              <Typography variant="h6">
                {user.firstName} {user.lastName}
              </Typography>
              <Typography variant="body2">{user.email}</Typography>
              <Typography variant="body2">ID: {user.idNo}</Typography>
            </Paper>

            <Paper elevation={0} square sx={{ p: 2 }}>
              <Box display="flex" justifyContent="center">
                <Calendar
                  onClickDay={handleDateClick}
                  tileContent={tileContent}
                />
              </Box>
            </Paper>
          </Box>

          {/* Carousel Section */}
          <Paper
            elevation={0}
            square
            sx={{
              flex: isTabletOrBelow ? '1 1 100%' : 4,
              p: 2,
              position: 'relative',
              overflow: 'hidden',
              height: isTabletOrBelow ? 'auto' : '69vh',
              minHeight: isTabletOrBelow ? '300px' : undefined,
            }}
          >
            <div className="carousel-container">
              {carouselSlides.length > 0 ? (
                carouselSlides.map((slide, index) => (
                  <div
                    key={index}
                    className={`carousel-slide ${
                      index === currentSlide ? 'active' : ''
                    }`}
                    style={{
                      backgroundImage: `url(${slide.image})`,
                      borderRadius: '5px',
                    }}
                  >
                    <div className="carousel-content">
                      <h2>{slide.title}</h2>
                      <p>{slide.description}</p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="carousel-slide fallback active">
                  <div className="carousel-content">
                    <img
                      src="/smulogo.png"
                      alt="SMU Logo"
                      style={{ width: 80 }}
                    />
                    <h2>Welcome to SMU Alumni Portal</h2>
                    <p>Stay connected. Stay informed.</p>
                  </div>
                </div>
              )}

              <div className="carousel-indicators">
                {carouselSlides.map((_, index) => (
                  <button
                    key={index}
                    className={`carousel-dot ${
                      index === currentSlide ? 'active' : ''
                    }`}
                    onClick={() => setCurrentSlide(index)}
                  />
                ))}
              </div>
            </div>
          </Paper>
        </Box>
      </Box>

      {/* Events Modal */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'background.paper',
            p: 4,
            borderRadius: 2,
            boxShadow: 24,
            width: { xs: 300, sm: 400 },
            maxHeight: '80vh',
            overflowY: 'auto',
          }}
        >
          <Typography variant="h6" mb={2}>
            Events on {selectedDate?.toDateString()}
          </Typography>
          {eventsOnSelectedDate.length ? (
            <List>
              {eventsOnSelectedDate.map((event, idx) => (
                <ListItem key={idx}>
                  <ListItemText
                    primary={event.title}
                    secondary={`${formatEventTimeRange(event)} • ${event.location}`}
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography>No events</Typography>
          )}
        </Box>
      </Modal>
    </Box>
  );
};

export default AlumniDashboard;
